/**
 * Rooms Page
 * Display available rooms and selection
 */

import { t, formatDate } from '../i18n';
import {
  Header,
  Card,
  RoomCard,
  ErrorMessage,
  StepIndicator,
  FloatingNav,
  ExtraChargeModal,
} from '../components';
import {
  getState,
  selectRoom,
  navigateTo,
  calculateNights,
  validateCapacityAndCalculateCharges,
  setExtraCharges,
} from '../store';
import { openLightbox } from '../components/lightbox';
import type { RoomAvailability } from '../types';

/**
 * Render rooms page
 */
export function renderRoomsPage(): string {
  const state = getState();
  const { search, availableRooms } = state;

  if (!search) {
    return ErrorMessage(t('errorGeneric'));
  }

  const nights = calculateNights(search.checkIn, search.checkOut);

  // Date summary
  const dateSummary = `
    <div class="date-summary">
      <div class="date-box">
        <span class="date-label">${t('checkIn')}</span>
        <span class="date-value">${formatDate(search.checkIn)}</span>
      </div>
      <div class="date-box">
        <span class="date-label">${t('checkOut')}</span>
        <span class="date-value">${formatDate(search.checkOut)}</span>
      </div>
      <div class="nights-badge">${nights} ${t('nights')}</div>
    </div>
  `;

  // No rooms available
  if (availableRooms.length === 0) {
    return `
      ${Header(t('roomsTitle'), false)}
      ${StepIndicator(2)}
      <div class="page-content">
        ${Card(dateSummary)}
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <p>${t('noRoomsAvailable')}</p>
        </div>
      </div>
      ${FloatingNav()}
    `;
  }

  // Room cards
  const roomCards = availableRooms
    .map((room: RoomAvailability, index: number) =>
      RoomCard(room, nights, `window.selectRoom(${index})`)
    )
    .join('');

  // Disable next button if no room selected
  const disableNext = !state.selectedRoom;

  return `
    ${Header(t('roomsTitle'), false)}
    ${StepIndicator(2)}
    <div class="page-content">
      ${Card(dateSummary, 'sticky-summary')}
      <div class="rooms-list" id="rooms-list">
        ${roomCards}
      </div>
    </div>

    <!-- Floating scroller (Top / Bottom) -->
    <div id="rooms-scroller" style="position:fixed; right:16px; bottom:24px; display:flex; flex-direction:column; gap:8px; z-index:2000;">
      <button id="rooms-scroll-to-top" aria-label="Scroll to top" style="display:none; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▲</button>
      <button id="rooms-scroll-to-bottom" aria-label="Scroll to bottom" style="display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▼</button>
    </div>

    ${FloatingNav(disableNext)}
  `;
}

/**
 * Initialize rooms page event handlers
 */
export function initRoomsPage(): void {
  const state = getState();

  // Back button handler
  window.handleBack = () => {
    navigateTo('search');
  };

  // Room selection handler with capacity validation
  window.selectRoom = (index: number) => {
    const room = state.availableRooms[index];
    if (!room || !state.search) return;

    selectRoom(room);

    // Validate capacity and calculate extra charges
    const extraCharges = validateCapacityAndCalculateCharges(room, state.search);

    if (extraCharges) {
      // Show confirmation modal
      setExtraCharges(extraCharges);
      showExtraChargeModal(extraCharges, room);
    } else {
      // No extra charges, proceed to guest info
      setExtraCharges(null);
      navigateTo('guest-info');
    }
  };

  // Expose forward action to footer: validate capacity and proceed to guest-info
  window.handleForward = () => {
    const current = getState();
    if (!current.selectedRoom || !current.search) {
      // No room selected, do nothing (button should be disabled)
      return;
    }

    // Validate capacity and calculate extra charges
    const extraCharges = validateCapacityAndCalculateCharges(current.selectedRoom, current.search);

    if (extraCharges) {
      // Show confirmation modal
      setExtraCharges(extraCharges);
      showExtraChargeModal(extraCharges, current.selectedRoom);
    } else {
      // No extra charges, proceed to guest info
      setExtraCharges(null);
      navigateTo('guest-info');
    }
  };

  // Floating scroller (Top / Bottom) for Rooms list
  try {
    const scrollerTop = document.getElementById('rooms-scroll-to-top') as HTMLButtonElement | null;
    const scrollerBottom = document.getElementById(
      'rooms-scroll-to-bottom'
    ) as HTMLButtonElement | null;
    const listEl = document.getElementById('rooms-list');
    if (listEl) {
      function updateScrollerVisibility() {
        if (!scrollerTop || !scrollerBottom || !listEl) return;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        scrollerTop.style.display = scrollTop > 150 ? 'flex' : 'none';
        const nearBottom =
          window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 150;
        scrollerBottom.style.display = nearBottom ? 'none' : 'flex';
      }

      scrollerTop?.addEventListener('click', () => {
        // Scroll to absolute top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      scrollerBottom?.addEventListener('click', () => {
        listEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        window.scrollTo({
          top: document.documentElement.scrollHeight - window.innerHeight,
          behavior: 'smooth',
        });
      });

      window.addEventListener('scroll', updateScrollerVisibility);
      window.addEventListener('resize', updateScrollerVisibility);
      // initial
      updateScrollerVisibility();

      // Load image carousels for each room card
      try {
        loadRoomCarousels();
      } catch (err) {
        console.warn('[Rooms] Failed to load room carousels', err);
      }
    }
  } catch (e) {
    // ignore
    console.error('[Rooms] Failed to init scroller', e);
  }
}

// Load and render carousels for each room card
async function loadRoomCarousels(): Promise<void> {
  try {
    const { getTenantId } = await import('../config');
    const tenantId = getTenantId();
    const containerEls = Array.from(document.querySelectorAll('.room-images')) as HTMLElement[];
    if (containerEls.length === 0) return;

    for (const el of containerEls) {
      const roomType = el.getAttribute('data-room-type');
      if (!roomType) continue;

      // Find matching room in state to get hotelCode
      const state = getState();
      const room = state.availableRooms.find(r => r.roomTypeCode === roomType);
      if (!room || !room.hotelCode) continue;

      try {
        const { getRoomImages } = await import('../api');
        const images = await getRoomImages(tenantId, room.hotelCode, roomType);
        if (images && images.length > 0) {
          renderCarousel(el, images.slice(0, 15));
        }
      } catch (err) {
        console.warn('[Rooms] Failed to fetch images for', roomType, err);
      }
    }
  } catch (err) {
    console.error('[Rooms] loadRoomCarousels error', err);
  }
}

// Render a simple swipeable carousel into container element
function renderCarousel(
  container: HTMLElement,
  images: Array<{ path: string; url: string; thumbUrl?: string; order?: number }>
) {
  if (!images || images.length === 0) return;

  // Single image case
  if (images.length === 1) {
    const img = images[0];
    container.innerHTML = `
      <div class="carousel">
        <div class="carousel-track">
          <div class="carousel-item" data-real-index="0">
            <img src="${img.thumbUrl || img.url}" alt="room image" loading="lazy" />
          </div>
        </div>
      </div>
    `;
    const itemEl = container.querySelector('.carousel-item') as HTMLElement;
    itemEl?.addEventListener('click', () => openLightbox(images, 0));
    return;
  }

  // Multiple images: Create Infinite Loop track with Clones
  // Item sequence: [ Clone of N-1, Img 0, Img 1, ..., Img N-1, Clone of 0 ]
  const totalCount = images.length;
  const cloneLast = images[totalCount - 1];
  const cloneFirst = images[0];

  const trackItems = [
    { ...cloneLast, isClone: true, realIndex: totalCount - 1 },
    ...images.map((img, i) => ({ ...img, isClone: false, realIndex: i })),
    { ...cloneFirst, isClone: true, realIndex: 0 },
  ];

  container.innerHTML = `
    <div class="carousel">
      <div class="carousel-track">
        ${trackItems
          .map(
            (img) => `
          <div class="carousel-item" data-real-index="${img.realIndex}">
            <img data-src="${img.thumbUrl || img.url}" alt="room image" loading="lazy" />
          </div>
        `
          )
          .join('')}
      </div>
      <button class="carousel-prev" aria-label="Previous">‹</button>
      <button class="carousel-next" aria-label="Next">›</button>
      <div class="carousel-indicators">
        ${images.map((_, i) => `<button class="indicator${i === 0 ? ' active' : ''}" data-index="${i}"></button>`).join('')}
      </div>
    </div>
  `;

  const track = container.querySelector('.carousel-track') as HTMLElement;
  const prev = container.querySelector('.carousel-prev') as HTMLElement;
  const next = container.querySelector('.carousel-next') as HTMLElement;
  const indicators = Array.from(container.querySelectorAll('.indicator')) as HTMLElement[];
  if (!track) return;

  // Lazy-load images
  track.querySelectorAll('img[data-src]').forEach((imgEl: Element) => {
    const img = imgEl as HTMLImageElement;
    img.src = img.dataset.src || '';
  });

  const getItemWidth = () => {
    return (track.querySelector('.carousel-item') as HTMLElement)?.clientWidth || track.clientWidth;
  };

  // Initial scroll position: start at index 1 (the first real image)
  requestAnimationFrame(() => {
    const itemWidth = getItemWidth();
    track.scrollLeft = itemWidth;
  });

  let isTeleporting = false;

  const handleScroll = () => {
    if (isTeleporting) return;
    const itemWidth = getItemWidth();
    if (!itemWidth) return;

    const currentTrackIndex = Math.round(track.scrollLeft / itemWidth);

    // Instant Teleport when reaching clones
    if (currentTrackIndex <= 0) {
      isTeleporting = true;
      track.scrollLeft = totalCount * itemWidth;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === totalCount - 1));
      setTimeout(() => {
        isTeleporting = false;
      }, 50);
    } else if (currentTrackIndex >= totalCount + 1) {
      isTeleporting = true;
      track.scrollLeft = 1 * itemWidth;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === 0));
      setTimeout(() => {
        isTeleporting = false;
      }, 50);
    } else {
      const realIndex = currentTrackIndex - 1;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === realIndex));
    }
  };

  track.addEventListener('scroll', handleScroll, { passive: true });

  // Next & Prev Arrow Clicks
  next?.addEventListener('click', (e) => {
    e.stopPropagation();
    const itemWidth = getItemWidth();
    track.scrollBy({ left: itemWidth, behavior: 'smooth' });
  });

  prev?.addEventListener('click', (e) => {
    e.stopPropagation();
    const itemWidth = getItemWidth();
    track.scrollBy({ left: -itemWidth, behavior: 'smooth' });
  });

  // Open Lightbox on image click
  const items = Array.from(track.querySelectorAll('.carousel-item')) as HTMLElement[];
  items.forEach((itemEl) => {
    itemEl.addEventListener('click', () => {
      const idx = parseInt(itemEl.getAttribute('data-real-index') || '0', 10);
      openLightbox(images, idx);
    });
  });

  // Indicator clicks
  indicators.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const realIdx = parseInt(btn.getAttribute('data-index') || '0', 10);
      const itemWidth = getItemWidth();
      track.scrollTo({ left: (realIdx + 1) * itemWidth, behavior: 'smooth' });
    });
  });
}

// Show extra charge confirmation modal
function showExtraChargeModal(
  extraCharges: NonNullable<ReturnType<typeof validateCapacityAndCalculateCharges>>,
  room: RoomAvailability
) {
  const modalHtml = ExtraChargeModal(
    extraCharges.extraAdults,
    extraCharges.extraChildren,
    extraCharges.extraAdultCharge,
    extraCharges.extraChildCharge,
    extraCharges.totalExtraCharge,
    room.currency || 'THB',
    () => {}, // Placeholder, will be replaced by window handler
    () => {} // Placeholder, will be replaced by window handler
  );

  // Insert modal into body
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalHtml;
  document.body.appendChild(modalContainer);

  // Handle confirm: proceed to guest info
  window.handleExtraChargeConfirm = () => {
    document.body.removeChild(modalContainer);
    navigateTo('guest-info');
  };

  // Handle cancel: go back to search
  window.handleExtraChargeCancel = () => {
    document.body.removeChild(modalContainer);
    setExtraCharges(null);
    navigateTo('search');
  };
}

// Extend window for global handlers
declare global {
  interface Window {
    handleBack: () => void;
    selectRoom: (index: number) => void;
    handleForward?: () => void;
    handleExtraChargeConfirm?: () => void;
    handleExtraChargeCancel?: () => void;
  }
}
